/**
 * 앱 상태.
 *
 * 재계산 범위를 파라미터 종류로 나눈 게 이 파일의 핵심이다.
 *   style    → 유니폼 값만 바꾼다. 재컴파일도 재계산도 없다. 즉각 반응.
 *   material → PBR 맵을 다시 굽는다. 1024px 기준 100ms 안팎.
 *   mesh     → 지오메트리를 다시 만든다. 해상도 128 기준 30ms 안팎.
 *   depth    → 깊이 추정부터 전부. 신경망이면 수 초.
 *
 * 이걸 안 나누면 러프니스 슬라이더 한 번에 신경망이 도는 앱이 된다.
 */

import { create } from 'zustand';
import * as THREE from 'three';
import type {
  DepthBackend,
  Field,
  ForgeParams,
  ImageAnalysis,
  InputMode,
  MaterialMaps,
  PresetId,
} from '../core/types.ts';
import type { HullMesh } from '../core/turnaround/hull.ts';
import { viewSampler, type ViewImage } from '../core/turnaround/views.ts';
import type { DepthOutcome } from '../core/depth/index.ts';
import type { ForgedMesh } from '../core/mesh/build.ts';
import type { ForgedMaterial } from '../core/pbr/material.ts';
import { applyProjection, applyStyleParams, buildPlaceholderMaterial, updateMaterialMaps } from '../core/pbr/material.ts';
import { DEFAULT_PARAMS, resolveParams } from '../core/presets.ts';
import {
  buildFromDepth,
  buildFromTurnaround,
  decodeAndAnalyze,
  estimateViewDepths,
  decodeSheet,
  decodeViews,
  runDepth,
  type StageReport,
  type TurnaroundInput,
} from '../core/pipeline.ts';
import { buildMaterialMaps } from '../core/pbr/maps.ts';
import { maskSampler, rigMesh, type Rig } from '../core/rig/index.ts';
import { bakeTexture } from '../core/turnaround/bake.ts';
import { anchorsInObject, fitPartToBody, shrinkBodyHead, skinPartToHead, transformPart, type AttachFit } from '../core/parts/attach.ts';
import { buildMaterial } from '../core/pbr/material.ts';
import { decimate } from '../core/mesh/decimate.ts';

type ParamKey = keyof ForgeParams;
type Scope = 'style' | 'material' | 'mesh' | 'depth';

const STYLE_KEYS = new Set<ParamKey>([
  'toonSteps', 'toonMix', 'rimStrength', 'rimColor', 'iridescence', 'backTint',
]);
const MATERIAL_KEYS = new Set<ParamKey>([
  'delight', 'normalStrength', 'roughnessBias', 'metalnessBias', 'aoStrength', 'emissiveStrength',
]);
const MESH_KEYS = new Set<ParamKey>([
  'resolution', 'meshMode', 'depthScale', 'inflate', 'smooth',
  'hullResolution', 'hullSmooth', 'sideFacesLeft', 'hullRelief', 'hullCarve', 'bakeTexture', 'targetTriangles', 'hullTolerance', 'hullDilate', 'hullDepthRelief',
]);

function scopeOf(key: ParamKey): Scope {
  if (STYLE_KEYS.has(key)) return 'style';
  if (MATERIAL_KEYS.has(key)) return 'material';
  if (MESH_KEYS.has(key)) return 'mesh';
  return 'depth';
}

export type Status = 'idle' | 'working' | 'ready' | 'error';

export interface ForgeStore {
  status: Status;
  stage: StageReport | null;
  error: string | null;

  fileName: string | null;
  previewUrl: string | null;
  /** 단일 이미지(부풀리기)인지, 턴어라운드(비주얼 헐)인지 */
  mode: InputMode;
  turnaround: TurnaroundInput | null;
  /** 뷰별 깊이(깊이 깎기용). 백엔드별 캐시. */
  viewDepths: Partial<Record<DepthBackend, (Field | null)[]>>;

  analysis: ImageAnalysis | null;
  depth: DepthOutcome | null;
  maps: MaterialMaps | null;
  /** 아틀라스 공간의 원본 맵. maps 는 베이크되면 albedo 가 바뀌므로 재베이크는 이걸 쓴다. */
  atlasMaps: MaterialMaps | null;
  mesh: ForgedMesh | HullMesh | null;
  /** 뼈·스킨·클립. 메시가 바뀔 때마다 다시 세운다. */
  rig: Rig | null;
  /** 재생 중인 클립 이름. null 이면 바인드 포즈. */
  clip: string | null;
  /** 앱 수명 동안 하나. 텍스처만 갈아 끼운다 — 재컴파일이 17초짜리라서 (material.ts). */
  material: ForgedMaterial;
  /** 실제 맵이 한 번이라도 들어갔는가. false 면 1×1 더미가 물려 있다. */
  materialReady: boolean;

  /** 머리 파츠 — 클로즈업 8뷰로 따로 만든 고밀도 머리. 본체 스켈레톤을 공유한다. */
  part: PartState | null;
  /** 파츠 원본 입력 — 본체를 다시 만들면 파츠도 다시 붙인다. */
  partInput: { input: TurnaroundInput; analysis: ImageAnalysis; name: string } | null;
  /** 자동으로 맞춘 배율에 곱하는 보정. 1 이 자동. */
  partScale: number;

  params: ForgeParams;
  /** 사용자가 프리셋 위에 직접 얹은 값들. 프리셋을 바꿔도 살아남지 않는다. */
  overrides: Partial<ForgeParams>;
  effectivePreset: PresetId;
  backend: DepthBackend;
  /** 사용자가 백엔드를 직접 골랐는가. 안 골랐으면 턴어라운드 입력에서 신경망으로 자동 전환한다. */
  backendTouched: boolean;
  maxSize: number;
  anisotropy: number;

  timings: Record<string, number>;

  loadFile: (file: File) => Promise<void>;
  loadSheetFile: (file: File) => Promise<void>;
  loadViewFiles: (files: File[], azimuths?: number[]) => Promise<void>;
  /** 머리 클로즈업 8뷰를 얹는다. 본체가 이미 있어야 한다. */
  loadPartFiles: (files: File[], azimuths?: number[]) => Promise<void>;
  loadPartUrls: (views: { azimuth: number; url: string }[], name: string) => Promise<void>;
  clearPart: () => void;
  setPartScale: (v: number) => void;
  setMode: (mode: InputMode) => void;
  /** URL 을 받아서 연다. 클릭 순서를 지키기 위해 fetch 전에 세대를 올린다. */
  loadUrl: (url: string, name: string, mode?: InputMode) => Promise<void>;
  /** 방위별 URL 묶음(턴어라운드 갤러리)을 연다. */
  loadTurnUrls: (views: { azimuth: number; url: string }[], name: string) => Promise<void>;
  setPreset: (preset: PresetId) => void;
  setParam: <K extends ParamKey>(key: K, value: ForgeParams[K]) => void;
  setBackend: (backend: DepthBackend) => void;
  setAnisotropy: (v: number) => void;
  setClip: (clip: string | null) => void;
  reset: () => void;
}


/**
 * ?trace=1 단계 추적 — 콘솔 + localStorage('tf:trace') + 탭 제목.
 * 탭이 멈추면 콘솔은 못 읽지만 localStorage 는 다음 탭에서, 제목은 탭 목록에서 읽힌다.
 * (2026-09-04 React 개발 빌드 멈춤을 이걸로 잡았다.)
 */
export function traceLog(msg: string): void {
  if (typeof location === 'undefined' || !location.search.includes('trace=1')) return;
  const line = `[tf ${performance.now() | 0}ms] ${msg}`;
  try { document.title = line; } catch { /* 무시 */ }
  try {
    const prev = localStorage.getItem('tf:trace') ?? '';
    localStorage.setItem('tf:trace', (prev.length > 20000 ? prev.slice(-10000) : prev) + line + '\n');
  } catch { /* 무시 */ }
  console.log(line);
}

/** 늦게 끝난 옛 작업이 새 결과를 덮어쓰지 않게 하는 세대 번호. */
let generation = 0;
let debounceTimer: ReturnType<typeof setTimeout> | null = null;

export interface PartState {
  mesh: THREE.SkinnedMesh;
  fit: AttachFit;
  triangleCount: number;
  vertexCount: number;
  material: ForgedMaterial;
  name: string;
  dispose: () => void;
}

/**
 * 파츠(머리) 조립 — 파츠 뷰셋으로 헐을 굽고, 목을 맞춰 본체 좌표로 옮기고, 본체 스켈레톤에 묶는다.
 * 본체 머리는 오므려 새 머리 안에 숨긴다. 파츠 해상도는 본체와 따로 간다 — 머리는 작아서 같은
 * 셀 수라도 실제 밀도가 훨씬 높다.
 */
function buildPart(
  partInput: { input: TurnaroundInput; analysis: ImageAnalysis; name: string },
  body: { rig: Rig; geometry: THREE.BufferGeometry; front: ViewImage; frame: { offset: THREE.Vector3; scale: number } },
  params: ForgeParams,
  anisotropy: number,
  userScale = 1,
): PartState {
  // 머리는 작아서 같은 셀 수라도 밀도가 훨씬 높다. 깎기는 끄고(얼굴 오목부는 깊이 요철이 만든다),
  // 삼각형은 3만 5천으로 줄인다 — 안 줄이면 19만이라 본체보다 무겁다.
  const partParams: ForgeParams = {
    ...params,
    hullResolution: Math.max(96, Math.round(params.hullResolution * 0.75)),
    hullCarve: 0,
    // 얼굴 요철(코·눈두덩·입술)은 여기서 나온다. 본체보다 세게 — 클로즈업이라 깊이 추정이 정확하다.
    hullDepthRelief: Math.max(params.hullDepthRelief, 0.05),
    targetTriangles: 35000,
  };
  const maps = buildMaterialMaps(partInput.analysis, partParams);
  const hull = buildFromTurnaround({ ...partInput.input }, partParams, {});
  let geometry = hull.mesh.geometry;
  let albedo = maps.albedo, orm = maps.orm, normal = maps.normal, emissive = maps.emissive;
  if (params.bakeTexture) {
    // 얼굴은 정면 한 장이 거의 다 갖게 한다. 45° 뷰가 조금이라도 섞이면 눈·코가 두 벌로 갈라진다
    // (실측 2026-09-04: 파츠 첫 판이 얼굴 한복판에 세로 띠로 두 얼굴).
    const baked = bakeTexture(geometry, maps.albedo, partInput.input.atlas, hull.mesh.projection, { orm: maps.orm, normal: maps.normal, emissive: maps.emissive }, { frontBias: 200, seamKeep: 0.9 });
    geometry.dispose();
    geometry = baked.geometry;
    albedo = baked.albedo; orm = baked.extra.orm ?? orm; normal = baked.extra.normal ?? normal; emissive = baked.extra.emissive ?? emissive;
  }
  if (partParams.targetTriangles > 0 && (geometry.getIndex()?.count ?? 0) / 3 > partParams.targetTriangles) {
    const d = decimate(geometry, partParams.targetTriangles);
    geometry.dispose();
    geometry = d.geometry;
  }
  const partFront = partInput.input.set.views.find((v) => v.azimuth === 0) ?? partInput.input.set.views[0]!;
  const partFrame = { offset: hull.mesh.projection.offset, scale: hull.mesh.projection.scale };
  const auto = fitPartToBody(body.front, partFront, body.geometry, body.frame);
  const fit: AttachFit = { ...auto, scale: auto.scale * userScale };
  // 목 아래로 깊게 내려 본체 옷깃 안까지 넣는다 — 짧게 자르면 턱과 옷깃 사이가 빈다.
  transformPart(geometry, fit, partFrame, body.frame, 0.14);
  const anchors = anchorsInObject(fit, body.frame);
  shrinkBodyHead(body.geometry, anchors, 0.32, 0.05, 0.3);
  const headY = body.rig.layout.head.y;
  skinPartToHead(geometry, body.rig.skeleton, anchors.neckY, Math.max(headY, anchors.neckY + 0.05 * anchors.unit));
  const material = buildMaterial({ ...maps, albedo, orm, normal, emissive }, { ...partParams, meshMode: 'inflate' }, { anisotropy });
  const mesh = new THREE.SkinnedMesh(geometry, material.material);
  mesh.name = 'TexelForgeHead';
  mesh.frustumCulled = false;
  mesh.bind(body.rig.skeleton, body.rig.skinned.bindMatrix);
  const index = geometry.getIndex();
  return {
    mesh, fit, material, name: partInput.name,
    triangleCount: index ? index.count / 3 : 0,
    vertexCount: geometry.getAttribute('position').count,
    dispose: () => { geometry.dispose(); material.dispose(); },
  };
}

export const useForgeStore = create<ForgeStore>((set, get) => {
  const disposeCurrent = () => {
    const { mesh, rig, part } = get();
    part?.dispose();
    rig?.skeleton.dispose();
    mesh?.geometry.dispose();
  };

  /** 실제 재계산. scope 이하 단계만 다시 돈다. */
  const rebuild = async (scope: Scope) => {
    const state = get();
    if (!state.analysis) return;

    const gen = ++generation;
    set({ status: 'working', error: null });

    const onStage = (r: StageReport) => {
      if (gen === generation) set({ stage: r });
      traceLog(`${r.label}${r.detail ? ' · ' + r.detail : ''}`);
    };

    try {
      // 맵을 다시 구울 필요가 없으면 재사용한다.
      const reuseMaps = scope === 'mesh' && (state.atlasMaps ?? state.maps) ? { maps: (state.atlasMaps ?? state.maps)! } : undefined;

      let depth = state.depth;
      let built: { maps: MaterialMaps; mesh: ForgedMesh | HullMesh; timings: Record<string, number> };
      let atlasMaps: MaterialMaps | null = null;

      if (state.mode === 'sheet' && state.turnaround) {
        // 턴어라운드 경로: 깊이 추정이 없다. 형상은 실루엣 N장이 만든다.
        let maps = reuseMaps?.maps;
        if (!maps) {
          onStage({ stage: 'maps', label: 'PBR 맵 굽는 중' });
          maps = buildMaterialMaps(state.analysis, state.params);
        }
        let depths = state.viewDepths[state.backend];
        if ((state.params.hullCarve > 0 || state.params.hullDepthRelief > 0) && !depths) {
          depths = await estimateViewDepths(state.turnaround, { backend: state.backend, onStage, anisotropy: state.anisotropy });
          if (gen !== generation) return;
          set({ viewDepths: { ...get().viewDepths, [state.backend]: depths } });
        }
        const hull = buildFromTurnaround(state.turnaround, state.params, { onStage }, depths);
        let mesh: HullMesh = hull.mesh;
        let displayMaps = maps;
        if (state.params.bakeTexture) {
          onStage({ stage: 'material', label: '텍스처 베이크 중 (UV 아틀라스)' });
          const tb = performance.now();
          const baked = bakeTexture(mesh.geometry, maps.albedo, state.turnaround.atlas, mesh.projection, { orm: maps.orm, normal: maps.normal, emissive: maps.emissive });
          mesh.geometry.dispose();
          mesh = { ...mesh, geometry: baked.geometry, vertexCount: baked.vertexCount, baked: true, chartShare: baked.chartShare };
          displayMaps = { ...maps, albedo: baked.albedo, orm: baked.extra.orm ?? maps.orm, normal: baked.extra.normal ?? maps.normal, emissive: baked.extra.emissive ?? maps.emissive };
          hull.timings.bake = performance.now() - tb;
        }
        if (state.params.targetTriangles > 0 && mesh.triangleCount > state.params.targetTriangles) {
          onStage({ stage: 'mesh', label: `리메시 중 (${mesh.triangleCount.toLocaleString()} → ${state.params.targetTriangles.toLocaleString()} 삼각형)` });
          const td = performance.now();
          const d = decimate(mesh.geometry, state.params.targetTriangles);
          mesh.geometry.dispose();
          mesh = { ...mesh, geometry: d.geometry, vertexCount: d.vertices, triangleCount: d.triangles };
          hull.timings.remesh = performance.now() - td;
        }
        built = { maps: displayMaps, mesh, timings: hull.timings };
        atlasMaps = maps;
      } else {
        if (scope === 'depth' || !depth) {
          depth = await runDepth(state.analysis, {
            backend: state.backend,
            onStage,
            anisotropy: state.anisotropy,
          });
          if (gen !== generation) return;
        }
        built = buildFromDepth(state.analysis, depth, state.params, { onStage }, reuseMaps);
      }
      if (gen !== generation) {
        built.mesh.geometry.dispose();
        return;
      }

      onStage({ stage: 'material', label: '텍스처 올리는 중' });
      const tm = performance.now();
      const baked = 'baked' in built.mesh && built.mesh.baked === true;
      const projection = 'projection' in built.mesh && !baked ? built.mesh.projection : null;
      // 베이크했으면 albedo 텍스처가 메시마다 새로 나온다 — 재사용 중이어도 올려야 한다.
      if (!reuseMaps || baked) {
        updateMaterialMaps(state.material, built.maps, state.params, {
          anisotropy: state.anisotropy,
          cutout: state.mode !== 'sheet' && state.analysis.maskSource !== 'full',
          projection,
        });
      } else {
        applyProjection(state.material.uniforms, projection);
        applyStyleParams(state.material.uniforms, state.material.material, state.params);
      }
      built.timings.material = performance.now() - tm;

      // 리깅. 실루엣은 시트면 정면 패널, 단일 뷰면 마스크.
      onStage({ stage: 'material', label: '뼈대 세우는 중' });
      const tr = performance.now();
      const frontView = state.turnaround?.set.views.find((v) => v.azimuth === 0) ?? state.turnaround?.set.views[0];
      const sil = state.mode === 'sheet' && frontView
        ? viewSampler(frontView)
        : maskSampler(state.analysis.mask);
      const rig = rigMesh(built.mesh.geometry, state.material.material, sil);
      built.timings.rig = performance.now() - tr;
      onStage({ stage: 'material', label: '뼈대 완료' });

      // 파츠(머리)가 있으면 새 본체에 다시 붙인다 — 본체 지오메트리가 매번 새로 나오기 때문.
      let part: PartState | null = null;
      const partInput = state.partInput;
      if (partInput && frontView) {
        try {
          onStage({ stage: 'material', label: '머리 파츠 붙이는 중' });
          const tp = performance.now();
          const bodyProj = 'projection' in built.mesh ? built.mesh.projection : null;
          if (!bodyProj) throw new Error('본체가 턴어라운드 헐이 아닙니다');
          part = buildPart(partInput, { rig, geometry: built.mesh.geometry, front: frontView, frame: { offset: bodyProj.offset, scale: bodyProj.scale } }, state.params, state.anisotropy, state.partScale);
          rig.skinned.add(part.mesh);
          built.timings.part = performance.now() - tp;
        } catch (err) {
          traceLog('파츠 실패: ' + (err instanceof Error ? err.message : String(err)));
        }
      }

      disposeCurrent();
      set({
        status: 'ready',
        stage: null,
        part,
        depth,
        maps: built.maps,
        atlasMaps: atlasMaps ?? (reuseMaps?.maps ?? built.maps),
        mesh: built.mesh,
        rig,
        materialReady: true,
        timings: { ...state.timings, ...built.timings, depth: depth?.ms ?? 0 },
      });
      traceLog('상태 반영 완료');
    } catch (err) {
      if (gen !== generation) return;
      set({ status: 'error', error: err instanceof Error ? err.message : String(err), stage: null });
    }
  };

  const schedule = (scope: Scope, delay = 90) => {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => { void rebuild(scope); }, delay);
  };

  return {
    status: 'idle',
    stage: null,
    error: null,
    fileName: null,
    previewUrl: null,
    mode: 'single',
    turnaround: null,
    viewDepths: {},
    analysis: null,
    depth: null,
    maps: null,
    atlasMaps: null,
    mesh: null,
    rig: null,
    clip: 'idle',
    material: buildPlaceholderMaterial(),
    materialReady: false,
    part: null,
    partInput: null,
    partScale: 1,
    params: DEFAULT_PARAMS,
    overrides: {},
    effectivePreset: 'character',
    backend: 'heuristic',
    backendTouched: false,
    maxSize: 1024,
    anisotropy: 8,
    timings: {},

    async loadTurnUrls(views, name) {
      const gen = ++generation;
      set({ mode: 'sheet', status: 'working', error: null, stage: { stage: 'decode', label: `뷰 ${views.length}장 받는 중` } });
      try {
        const files = await Promise.all(views.map(async (v) => {
          const res = await fetch(v.url);
          if (!res.ok) throw new Error(`뷰 이미지를 못 받았습니다 (${res.status}).`);
          const blob = await res.blob();
          return new File([blob], `${name}-${String(v.azimuth).padStart(3, '0')}.webp`, { type: blob.type || 'image/webp' });
        }));
        if (gen !== generation) return;
        await get().loadViewFiles(files, views.map((v) => v.azimuth));
      } catch (err) {
        if (gen !== generation) return;
        set({ status: 'error', error: err instanceof Error ? err.message : String(err), stage: null });
      }
    },

    async loadUrl(url: string, name: string, mode: InputMode = 'single') {
      // 세대를 먼저 올린다. 네트워크가 늦게 오는 옛 클릭이 새 클릭을 덮어쓰지 못하게.
      const gen = ++generation;
      set({ mode });
      set({ status: 'working', error: null, stage: { stage: 'decode', label: '이미지 받는 중' } });
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`이미지를 못 받았습니다 (${res.status}).`);
        const blob = await res.blob();
        if (gen !== generation) return;
        const file = new File([blob], name, { type: blob.type || 'image/webp' });
        await (mode === 'sheet' ? get().loadSheetFile(file) : get().loadFile(file));
      } catch (err) {
        if (gen !== generation) return;
        set({ status: 'error', error: err instanceof Error ? err.message : String(err), stage: null });
      }
    },

    async loadFile(file: File) {
      const gen = ++generation;
      const prevUrl = get().previewUrl;
      if (prevUrl) URL.revokeObjectURL(prevUrl);

      set({
        status: 'working',
        error: null,
        mode: 'single',
        turnaround: null,
        viewDepths: {},
        fileName: file.name,
        previewUrl: URL.createObjectURL(file),
        // 새 이미지에는 새 판단을 — 이전 이미지에 맞춰 손댄 값은 버린다.
        overrides: {},
      });

      try {
        const onStage = (r: StageReport) => { if (gen === generation) set({ stage: r }); };
        const { analysis, timings } = await decodeAndAnalyze(file, get().maxSize, onStage);
        if (gen !== generation) return;

        const { params, effective } = resolveParams(get().params.preset, analysis.stats, {});
        set({ analysis, params, effectivePreset: effective, depth: null, maps: null, atlasMaps: null, timings });

        await rebuild('depth');
      } catch (err) {
        if (gen !== generation) return;
        set({ status: 'error', error: err instanceof Error ? err.message : String(err), stage: null });
      }
    },

    async loadSheetFile(file: File) {
      const gen = ++generation;
      const prevUrl = get().previewUrl;
      if (prevUrl) URL.revokeObjectURL(prevUrl);
      set({
        status: 'working',
        error: null,
        mode: 'sheet',
        fileName: file.name,
        previewUrl: URL.createObjectURL(file),
        overrides: {},
      });
      try {
        const onStage = (r: StageReport) => { if (gen === generation) set({ stage: r }); };
        const { input, analysis, timings } = await decodeSheet(file, onStage);
        if (gen !== generation) return;
        const { params, effective } = resolveParams(get().params.preset, analysis.stats, {}, true);
        const backend: DepthBackend = get().backendTouched ? get().backend : 'onnx';
        set({ turnaround: input, viewDepths: {}, analysis, params, effectivePreset: effective, depth: null, maps: null, atlasMaps: null, timings, backend });
        await rebuild('mesh');
      } catch (err) {
        if (gen !== generation) return;
        set({ status: 'error', error: err instanceof Error ? err.message : String(err), stage: null });
      }
    },

    async loadViewFiles(files: File[], azimuths?: number[]) {
      const gen = ++generation;
      const prevUrl = get().previewUrl;
      if (prevUrl) URL.revokeObjectURL(prevUrl);
      set({
        status: 'working',
        error: null,
        mode: 'sheet',
        fileName: `${files.length}뷰 · ${files[0]?.name ?? ''}`,
        previewUrl: files[0] ? URL.createObjectURL(files[0]) : null,
        overrides: {},
      });
      try {
        const onStage = (r: StageReport) => { if (gen === generation) set({ stage: r }); };
        const { input, analysis, timings } = await decodeViews(files, onStage, azimuths);
        if (gen !== generation) return;
        const { params, effective } = resolveParams(get().params.preset, analysis.stats, {}, true);
        // 턴어라운드(생성 이미지, 균일 조명)는 Depth-Anything 이 잘 맞는다. 셰이딩 휴리스틱은 어두운
        // 천을 골로 오해해 깎는다(실측: 남색 망토가 함몰). 사용자가 직접 고르지 않았으면 신경망으로.
        const backend: DepthBackend = get().backendTouched ? get().backend : 'onnx';
        set({ turnaround: input, viewDepths: {}, analysis, params, effectivePreset: effective, depth: null, maps: null, atlasMaps: null, timings, backend });
        await rebuild('mesh');
      } catch (err) {
        if (gen !== generation) return;
        set({ status: 'error', error: err instanceof Error ? err.message : String(err), stage: null });
      }
    },

    async loadPartFiles(files: File[], azimuths?: number[]) {
      if (!get().turnaround) { set({ error: '머리 파츠는 본체(턴어라운드)를 먼저 만든 뒤에 얹을 수 있습니다.' }); return; }
      set({ status: 'working', error: null, stage: { stage: 'decode', label: `머리 뷰 ${files.length}장 읽는 중` } });
      try {
        const { input, analysis } = await decodeViews(files, (r) => set({ stage: r }), azimuths);
        set({ partInput: { input, analysis, name: `${files.length}뷰 머리` } });
        await rebuild('mesh');
      } catch (err) {
        set({ status: 'error', error: err instanceof Error ? err.message : String(err), stage: null });
      }
    },

    async loadPartUrls(views, name) {
      if (!get().turnaround) { set({ error: '머리 파츠는 본체(턴어라운드)를 먼저 만든 뒤에 얹을 수 있습니다.' }); return; }
      set({ status: 'working', error: null, stage: { stage: 'decode', label: `머리 뷰 ${views.length}장 받는 중` } });
      try {
        const files = await Promise.all(views.map(async (v) => {
          const res = await fetch(v.url);
          const blob = await res.blob();
          return new File([blob], `${name}-${String(v.azimuth).padStart(3, '0')}.webp`, { type: blob.type || 'image/webp' });
        }));
        await get().loadPartFiles(files, views.map((v) => v.azimuth));
      } catch (err) {
        set({ status: 'error', error: err instanceof Error ? err.message : String(err), stage: null });
      }
    },

    setPartScale(v: number) {
      set({ partScale: Math.max(0.7, Math.min(1.4, v)) });
      if (get().partInput) schedule('mesh', 200);
    },

    clearPart() {
      const p = get().part;
      if (p) { p.mesh.removeFromParent(); p.dispose(); }
      set({ part: null, partInput: null });
      void rebuild('mesh');
    },

    setMode(mode: InputMode) {
      set({ mode });
    },

    setPreset(preset: PresetId) {
      const { analysis } = get();
      const { params, effective } = resolveParams(preset, analysis?.stats ?? null, {}, get().mode === 'sheet' && Boolean(get().turnaround));
      set({ params, effectivePreset: effective, overrides: {} });
      if (analysis) schedule('material', 40);
    },

    setParam(key, value) {
      const state = get();
      const params = { ...state.params, [key]: value };
      const overrides = { ...state.overrides, [key]: value };
      set({ params, overrides });

      const scope = scopeOf(key);
      if (scope === 'style') {
        // 유니폼만 건드린다 — 프레임을 하나도 안 흘린다.
        applyStyleParams(state.material.uniforms, state.material.material, params);
        return;
      }
      if (!state.analysis) return;
      schedule(scope, scope === 'mesh' ? 60 : 120);
    },

    setBackend(backend: DepthBackend) {
      set({ backend, backendTouched: true, depth: null });
      if (get().analysis) schedule('depth', 0);
    },

    setClip(clip: string | null) {
      set({ clip });
    },

    setAnisotropy(v: number) {
      set({ anisotropy: v });
      for (const t of get().material.textures) t.anisotropy = v;
    },

    reset() {
      generation++;
      disposeCurrent();
      const url = get().previewUrl;
      if (url) URL.revokeObjectURL(url);
      set({
        status: 'idle', stage: null, error: null, fileName: null, previewUrl: null,
        mode: 'single', turnaround: null, viewDepths: {}, part: null, partInput: null, analysis: null, depth: null, maps: null, atlasMaps: null, mesh: null, rig: null, materialReady: false,
        params: DEFAULT_PARAMS, overrides: {}, timings: {},
      });
    },
  };
});
